import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Alert, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { useAppSelector, useRole } from 'hooks';
import { AccessRoles, BillingTabs } from 'constants/enums';
import { getCurrentGroup, isPrepaidOrg } from 'store/groups/reducer';
import { selectBillingPaymentMethods } from 'store/billing/reducer';
import { fetchBillingPaymentMethods } from 'store/billing/service';
import { isOrganization } from 'utils/Groups';
import { getPath } from 'routes';

import { useBillingGid } from '../hooks';

/**
 * The signup wizard no longer asks for a card (Charles #7): nothing is charged at signup, so a
 * payment method is asked for when there is something to pay for. This is what asks. Without it an
 * organisation could reach its first monthly invoice with no method on file and go straight into
 * dunning, which is the one exposure dropping the payment step created.
 *
 * It replaces the agreement acknowledgement in this slot for self-serve practices, which no longer
 * need one (D64 names the signer at signup). Dismissible for the session and never a gate — this is
 * a clinician-facing portal, and money must not stand between a nurse and a patient's readings.
 */
const PaymentMethodPromptBanner = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const gid = useBillingGid();
  const currentGroup = useAppSelector(getCurrentGroup);
  const prepaid = useAppSelector(isPrepaidOrg);
  const hasBillingAccess = useRole([AccessRoles.BILLING]);
  const methods = useAppSelector(selectBillingPaymentMethods);
  const [dismissed, setDismissed] = useState(false);

  const isOrg = !!currentGroup && currentGroup.gid !== 'all' && isOrganization(currentGroup);
  const applicable = !!gid && isOrg && prepaid && hasBillingAccess;

  useEffect(() => {
    // Only fetch where the banner could possibly show, so nothing else pays for it.
    if (applicable && !methods.data && !methods.loading) {
      dispatch(fetchBillingPaymentMethods(gid) as never);
    }
  }, [applicable, gid, methods.data, methods.loading, dispatch]);

  // An unanswered fetch is not an empty wallet: say nothing until the answer is in.
  if (!applicable || dismissed || methods.loading || !methods.data) return null;
  if (methods.data.length > 0) return null;

  // Yellow, not blue: this warns that something is missing rather than stating a fact
  // (Michel, 2026-09-11).
  return (
    <Alert variant="warning" className="mb-3" data-test="billing-payment-prompt">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="me-3">
          <strong>{intl.formatMessage({ id: 'billingPaymentPromptTitleKey' })}</strong>
          <div className="small">{intl.formatMessage({ id: 'billingPaymentPromptBodyKey' })}</div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="link"
            onClick={() => setDismissed(true)}
            data-test="billing-payment-prompt-later"
          >
            {intl.formatMessage({ id: 'billingAgreementLaterKey' })}
          </Button>
          <Button
            as={Link as any}
            to={getPath().dashboard.billing(BillingTabs.SETTINGS)}
            variant="primary"
            data-test="billing-payment-prompt-add"
          >
            {intl.formatMessage({ id: 'billingPaymentPromptCtaKey' })}
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default PaymentMethodPromptBanner;
